import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, Navigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import TableBox from '../../../components/TableBox';
import StatusBadge from '../../../components/StatusBadge';
import RowActions from '../../../components/RowActions';
import Modal from '../../../components/Modal';
import Button from '../../../components/Button';
import Input from '../../../components/Input';
import Toast from '../../../components/Toast';
import useFetch from '../../../hooks/useFetch';
import usePagination from '../../../hooks/usePagination';
import { usePermission } from '../../../hooks/usePermission';
import AccessReviewService from '../../../services/access-review.service';
import { HeadCellType, TableContextProvider } from '../../../interfaces/common';
import { SortOrder, Modules, AuthRoutes, NonAuthRoutes } from '../../../enums';
import useSorting from '../../../hooks/useSorting';
import { sortData } from '../../../utils/sortUtils';

interface AccessReviewRow {
  id: number;
  name: string;
  scope: 'global' | 'tenant';
  tenant_id: number | null;
  status: 'in_progress' | 'complete';
  total_users: number;
  certified_count: number;
  revoked_count: number;
  skipped_count: number;
  completed_at: string | null;
}

const AccessReviewList: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const canManage = usePermission('access-review:manage');

  const { page, perPage, setPage, setPerPage } = usePagination({ defaultPage: 1, defaultPerPage: 10 });
  const { order, orderBy, setOrder, setOrderBy } = useSorting({ defaultOrder: SortOrder.desc, defaultOrderBy: 'id' });
  const [items, setItems] = useState<AccessReviewRow[]>([]);
  const [total, setTotal] = useState(0);
  const [refreshToken, setRefreshToken] = useState(0);
  const [startModalOpen, setStartModalOpen] = useState(false);
  const [reviewName, setReviewName] = useState('');
  const [starting, setStarting] = useState(false);

  const { loading } = useFetch({
    service: AccessReviewService,
    func: 'list',
    payload: { page, limit: perPage },
    dependency: [refreshToken, page, perPage],
    onSuccess: (data: any) => {
      setItems(data?.items ?? []);
      setTotal(data?.pagination?.total ?? data?.items?.length ?? 0);
    },
    onError: () => {
      setItems([]);
    },
  });

  const closeStartModal = useCallback(() => {
    setStartModalOpen(false);
    setReviewName('');
  }, []);

  const handleStartReview = useCallback(async () => {
    setStarting(true);
    try {
      const response: any = await AccessReviewService.start(reviewName.trim() || undefined);
      closeStartModal();
      navigate(`${Modules.iam}${AuthRoutes.accessReviews}/${response?.data?.id}`);
    } catch (err) {
      // interceptor already surfaces the error toast
    } finally {
      setStarting(false);
    }
  }, [reviewName, closeStartModal, navigate, t]);

  const handleDownload = useCallback(
    async (row: AccessReviewRow) => {
      try {
        await AccessReviewService.downloadRecord(row.id, `access-review-${row.id}.json`);
      } catch (err) {
        toast.error(<Toast message={t('iam.accessReviews.list.pageTexts.downloadError')} />, { icon: false });
      }
    },
    [t]
  );

  const headCells: HeadCellType[] = useMemo(
    () => [
      { id: 'name', sortEnabled: true, width: '25%', label: t('iam.accessReviews.list.pageTexts.table.columns.name') },
      { id: 'status', sortEnabled: true, width: '13%', label: t('iam.accessReviews.list.pageTexts.table.columns.status') },
      { id: 'total_users', sortEnabled: true, width: '12%', label: t('iam.accessReviews.list.pageTexts.table.columns.totalUsers') },
      { id: 'certified_count', sortEnabled: true, width: '12%', label: t('iam.accessReviews.list.pageTexts.table.columns.certified') },
      { id: 'revoked_count', sortEnabled: true, width: '12%', label: t('iam.accessReviews.list.pageTexts.table.columns.revoked') },
      { id: 'completed_at', sortEnabled: true, width: '14%', label: t('iam.accessReviews.list.pageTexts.table.columns.completedAt') },
      { id: 'actions', sortEnabled: false, width: '12%', label: t('iam.accessReviews.list.pageTexts.table.columns.actions'), textAlign: 'right' },
    ],
    [t]
  );

  const sortedItems = useMemo(() => sortData(items, orderBy, order), [items, orderBy, order]);

  const Row = useCallback(
    ({ item, className }: { item: AccessReviewRow; className?: string }) => (
      <tr className={className}>
        <td>{item.name}</td>
        <td>
          <StatusBadge
            label={
              item.status === 'complete'
                ? t('iam.accessReviews.list.pageTexts.statusComplete')
                : t('iam.accessReviews.list.pageTexts.statusInProgress')
            }
            colour={item.status === 'complete' ? '#2e7d32' : '#ed6c02'}
          />
        </td>
        <td>{item.total_users}</td>
        <td>{item.certified_count}</td>
        <td>{item.revoked_count}</td>
        <td>{item.completed_at ? new Date(item.completed_at).toLocaleString() : '—'}</td>
        <td className="text-end">
          <RowActions
            actions={[
              {
                id: 'open',
                label: t('iam.accessReviews.list.pageTexts.open') || 'Open Review',
                icon: <i className="ri-eye-line" />,
                onClick: () => navigate(`${Modules.iam}${AuthRoutes.accessReviews}/${item.id}`),
              },
              item.status === 'complete' && {
                id: 'download',
                label: t('iam.accessReviews.list.pageTexts.download') || 'Download Summary',
                icon: <i className="ri-download-line" />,
                onClick: () => handleDownload(item),
              },
            ]}
          />
        </td>
      </tr>
    ),
    [navigate, handleDownload, t]
  );

  const values: TableContextProvider = useMemo(
    () => ({
      headCells,
      row: Row,
      order,
      orderBy,
      page,
      perPage,
      total: total || sortedItems.length,
      loading,
      data: sortedItems,
      pagination: true,
      noDataFoundMessage: t('iam.accessReviews.list.pageTexts.table.noDataFoundMessage'),
      setPage,
      setPerPage,
      setOrder,
      setOrderBy,
    }),
    [headCells, Row, order, orderBy, page, perPage, total, sortedItems, loading, setPage, setPerPage, setOrder, setOrderBy, t]
  );

  if (!canManage) {
    return <Navigate replace to={{ pathname: Modules.error + NonAuthRoutes.forbidden }} />;
  }

  return (
    <>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h4 className="mb-0">{t('iam.accessReviews.list.pageTexts.title')}</h4>
        <Button
          className="btn btn-primary"
          text={t('iam.accessReviews.list.pageTexts.startNew')}
          onClick={() => setStartModalOpen(true)}
        />
      </div>

      <TableBox tableValues={values} />

      <Modal
        open={startModalOpen}
        onClose={closeStartModal}
        title={t('iam.accessReviews.list.pageTexts.startModal.title')}
        content={
          <Input
            id="review-name"
            name="review-name"
            label={t('iam.accessReviews.list.pageTexts.startModal.nameLabel')}
            placeholder={t('iam.accessReviews.list.pageTexts.startModal.namePlaceholder')}
            value={reviewName}
            onChange={(e: any) => setReviewName(e.target.value)}
          />
        }
        action={
          <>
            <Button className="btn btn-outline-secondary" text={t('common.buttons.cancel')} onClick={closeStartModal} />
            <Button
              className="btn btn-primary"
              text={t('iam.accessReviews.list.pageTexts.startModal.confirm')}
              onClick={handleStartReview}
              loading={starting}
            />
          </>
        }
      />
    </>
  );
};

export default AccessReviewList;
